import axios from 'axios';

// Helper to clean up URL
const cleanUrl = (url) => {
  if (!url) return '';
  return url.replace(/\/+$/, '');
};

// IMPORTANT: The gateway already adds the /api prefix when forwarding requests
// Do NOT include /api in the baseURL as it will cause double prefixing
const baseURL = cleanUrl(process.env.REACT_APP_API_URL) || 'http://localhost:9900';

// Base URL for API requests

// Create axios instance with improved configuration
const apiClient = axios.create({
  baseURL,
  timeout: 180000, // Increased timeout to 180 seconds for batch operations
  withCredentials: true,
  headers: {
    'Accept': 'application/json'
  },
  // Add better retry and timeout handling
  validateStatus: status => status < 500, // Only reject if server error
  // Add better timeout handling
  timeoutErrorMessage: 'Request timed out. The server may still be processing your request.',
  // Ensure data is properly serialized for all request types
  transformRequest: [(data, headers) => {
    if (data && headers['Content-Type'] === 'application/json') {
      return JSON.stringify(data);
    }
    return data;
  }]
});

// Add request interceptor for auth
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    const apiKey = process.env.REACT_APP_API_KEY;
    
    // Add token to headers if available
    if (token) {
      // Ensure token has Bearer prefix
      const formattedToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      config.headers.Authorization = formattedToken;
    }
    
    // Add API key to headers if available
    if (apiKey) {
      config.headers['x-api-key'] = apiKey;
    } else {
      // No API key available for this request
      
      // For development/testing, use a fallback API key
      if (process.env.NODE_ENV !== 'production') {
        config.headers['x-api-key'] = 'local-development-api-key';
      }
    }
    
    // Handle Content-Type header based on data type
    if (config.data instanceof FormData) {
      console.log('FormData detected, removing Content-Type header');
      delete config.headers['Content-Type'];
      console.log('Headers after removal:', config.headers);
    } else if (config.data && typeof config.data === 'object') {
      // Set Content-Type for JSON data
      config.headers['Content-Type'] = 'application/json';
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for error handling
// NOTE: validateStatus allows 4xx through as "success", so we must check here
apiClient.interceptors.response.use(
  (response) => {
    // Reject 4xx responses that slipped through validateStatus
    if (response.status >= 400) {
      if (response.status === 401) {
        localStorage.removeItem('token');
      }
      const error = new Error(response.data?.error || response.data?.message || `Request failed with status ${response.status}`);
      error.response = response;
      return Promise.reject(error);
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
    }
    return Promise.reject(error);
  }
);

// Add response interceptor for better error handling
apiClient.interceptors.response.use(
  response => {
    // Process successful responses
    return response;
  },
  error => {
    // Handle error responses without excessive logging
    if (error.response) {
      // Server responded with a status code outside the 2xx range
      // Error details available in error.response
    } else if (error.request) {
      // Request was made but no response was received
      // Error details available in error.request
    } else {
      // Something happened in setting up the request
      // Error details available in error.message
    }
    return Promise.reject(error);
  }
);

export default apiClient;
